import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShowFavoritesPage } from '@account/pages/Favorites/hooks';
import { useAccountDebugMenu } from '@account/pages/AccountDebugMenu/hooks';
import {
  BuyingIcon,
  SellingIcon,
  PortfolioIcon,
  ProfileIcon,
  FollowingIcon,
  SettingsIcon,
  HelpIcon,
  LogoutIcon,
  SecurityIcon,
  NotificationIcon,
} from '@stockx/icons';

import { useBreakpointValue } from '@stockx/ui';

import { PageSwitcherTypes, useUrlBuilder } from '@app/router';
import { useIsNonInteractiveViewer } from '@app/ui';
import { useInitiateUserLogout } from '@app/user';
import { EVENTS } from '@app/segment';

import { MenuItem } from '../../shared/types/menu';

const iconStyles = { marginRight: 4, boxSize: 7 };

type useAccountMenuReturn = {
  items: MenuItem[];
};

const useAccountMenu = (): useAccountMenuReturn => {
  const { t } = useTranslation();
  const isNonInteractiveViewer = useIsNonInteractiveViewer();
  const initiateUserLogout = useInitiateUserLogout();
  const showDebugMenu = useAccountDebugMenu();
  const buildUrl = useUrlBuilder();
  const { showFavoritesPage, milestone } = useShowFavoritesPage();
  const { isMobile } = useBreakpointValue();
  const favoritesPageUrl =
    milestone === 2 ? '/favorites' : '/favorites/all-favorites';

  const items = useMemo(() => {
    const menuItems: MenuItem[] = [
      {
        title: t('Security'),
        path: buildUrl(`/security`),
        icon: <SecurityIcon {...iconStyles} />,
        description: t('account_screen_desc_security'),
        eventLabel: EVENTS.ACCOUNT_SECURITY_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: t('Buying'),
        path: buildUrl(`/buying/current`),
        icon: <BuyingIcon {...iconStyles} />,
        description: t('account_screen_desc_buying'),
        eventLabel: EVENTS.ACCOUNT_BUYING_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: t('Selling'),
        path: buildUrl(`/selling`),
        icon: <SellingIcon {...iconStyles} />,
        description: t('account_screen_desc_selling'),
        eventLabel: EVENTS.ACCOUNT_SELLING_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: t('Profile'),
        path: buildUrl(`/profile`),
        icon: <ProfileIcon {...iconStyles} />,
        description: t('account_screen_desc_profile'),
        eventLabel: EVENTS.ACCOUNT_PROFILE_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: t('Message Center'),
        path: buildUrl(`/message-center`),
        icon: <NotificationIcon {...iconStyles} />,
        description: t('See the latest news'),
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: t('Portfolio'),
        path: buildUrl(`/portfolio`),
        icon: <PortfolioIcon {...iconStyles} />,
        description: t('account_screen_desc_portfolio'),
        eventLabel: EVENTS.ACCOUNT_PORTFOLIO_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: showFavoritesPage ? t('Favorites') : t('Following'),
        path: showFavoritesPage
          ? buildUrl(favoritesPageUrl)
          : buildUrl(`/following`),
        icon: <FollowingIcon {...iconStyles} />,
        description: showFavoritesPage
          ? t('account_screen_desc_favorites')
          : t('account_screen_desc_following'),
        eventLabel: EVENTS.ACCOUNT_FOLLOWING_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      {
        title: t('Settings'),
        path: buildUrl(`/settings`),
        icon: <SettingsIcon {...iconStyles} />,
        description: t('account_screen_desc_settings'),
        eventLabel: EVENTS.ACCOUNT_SETTINGS_CLICKED,
        linkType: PageSwitcherTypes.Bypass,
      },
      ...(showDebugMenu && isMobile
        ? [
            {
              title: 'Debug Menu',
              path: buildUrl(`/debug-menu`),
              icon: <HelpIcon {...iconStyles} />,
              description: t('account_screen_desc_debug_menu'),
              eventLabel: EVENTS.ACCOUNT_DEBUG_MENU_CLICKED,
              linkType: PageSwitcherTypes.Bypass,
            },
          ]
        : []),
      {
        title: t('account_screen_desc_logout'),
        path: isNonInteractiveViewer ? '/signout' : '',
        icon: <LogoutIcon {...iconStyles} />,
        linkType: PageSwitcherTypes.Bypass,
        eventLabel: EVENTS.ACCOUNT_LOGOUT_CLICKED,
        onClick: () => {
          initiateUserLogout();
        },
      },
    ];

    return menuItems;
  }, [buildUrl, initiateUserLogout, isNonInteractiveViewer, t]);

  return { items };
};

export default useAccountMenu;
